import React, {
  useEffect,
  useState,
  useMemo,
  useRef,
  useCallback,
} from "react";

import { useTranslation } from "react-i18next";
import { Col, Row, Container } from "react-bootstrap";
import BreadcrumbLayout from "../../component/Breadcrumb";

import {
  Form,
  Select,
  Image,
  Pagination,
  Empty,
} from "antd";

import { ImageGalleryService } from "../../Services/ImageGalleryService";

import demoUser from "../../assets/images/broken-img.jpg";
import loadingImg from "../../assets/images/loading-img.gif";
import Animation from "../../assets/images/Animation.gif";

import PhotoGallerySidebar from "./PhotoGallerySidebar";

export default function ImageGallery() {
  const { t, i18n } = useTranslation();

  // ---------------------------------------------------------
  // Translation / Menu
  // ---------------------------------------------------------

  const menuData = useMemo(
    () => t("menu.media-gallery", { returnObjects: true }) || {},
    [t, i18n.language]
  );

  // ---------------------------------------------------------
  // Service
  // ---------------------------------------------------------

  const imageGalleryService = useMemo(
    () => new ImageGalleryService(),
    []
  );

  // ---------------------------------------------------------
  // Language
  // ---------------------------------------------------------

  const [lang, setLang] = useState(
    localStorage.getItem("language") === "en" ? 2 : 1
  );

  // ---------------------------------------------------------
  // Form
  // ---------------------------------------------------------

  const [form] = Form.useForm();

  // ---------------------------------------------------------
  // Gallery Data
  //
  // allGalleryData:
  // API से आने वाला पूरा data सिर्फ एक बार store होगा।
  // ---------------------------------------------------------

  const [allGalleryData, setAllGalleryData] = useState([]);

  // ---------------------------------------------------------
  // Filtered Data
  // ---------------------------------------------------------

  const [filteredData, setFilteredData] = useState([]);

  // ---------------------------------------------------------
  // Category List
  // ---------------------------------------------------------

  const [filterListData, setFilterListData] = useState([]);

  const [filterType, setFilterType] = useState("-1");

  // ---------------------------------------------------------
  // Loading states
  // ---------------------------------------------------------

  const [loadingDDL, setLoadingDDL] = useState(true);

  const [loadingGallery, setLoadingGallery] = useState(false);

  // ---------------------------------------------------------
  // Pagination
  // ---------------------------------------------------------

  const [currentPage, setCurrentPage] = useState(1);

  const [itemsPerPage, setItemsPerPage] = useState(9);

  // ---------------------------------------------------------
  // Image Cache
  //
  // docUniqueId -> Blob URL
  //
  // एक image एक बार download होने के बाद दोबारा download
  // नहीं होगी।
  // ---------------------------------------------------------

  const imageCache = useRef(new Map());

  // ---------------------------------------------------------
  // Current page image URLs
  // ---------------------------------------------------------

  const [pageImages, setPageImages] = useState({});

  // =========================================================
  // LANGUAGE CHANGE
  // =========================================================

  useEffect(() => {
    const currentLanguage =
      localStorage.getItem("language") === "en" ? 2 : 1;

    setLang(currentLanguage);
  }, [i18n.language]);

  // =========================================================
  // LOAD CATEGORY LIST
  // =========================================================

  useEffect(() => {
    let isMounted = true;

    const getContentTypes = async () => {
      try {
        setLoadingDDL(true);

        const res =
          await imageGalleryService.getCommonList(
            "imageCategoryType",
            lang
          );

        if (!isMounted) return;

        const data = [
          {
            value: "-1",
            name: lang === 1 ? "-- सभी --" : "-- Any --",
            label: lang === 1 ? "-- सभी --" : "-- Any --",
            nameHindi: "",
            type: "",
            extraField1: "",
          },
        ];

        if (Array.isArray(res?.data)) {
          res.data.forEach((item) => {
            data.push({
              value: String(item.value),
              name: item.name,
              label: item.label,
              nameHindi: item.nameHindi,
              type: item.type,
              extraField1: item.extraField1,
            });
          });
        }

        setFilterListData(data);
      } catch (error) {
        console.error(
          "Error loading image category:",
          error
        );
      } finally {
        if (isMounted) {
          setLoadingDDL(false);
        }
      }
    };

    getContentTypes();

    return () => {
      isMounted = false;
    };
  }, [imageGalleryService, lang]);

  // =========================================================
  // LOAD ALL GALLERY DATA
  //
  // IMPORTANT:
  //
  // यहाँ imageCategoryType = "-1"
  //
  // इसका मतलब सभी category का data एक साथ API से आएगा।
  //
  // यह API language बदलने पर ही दोबारा चलेगी।
  // Dropdown change होने पर यह API नहीं चलेगी।
  // =========================================================

  useEffect(() => {
    let isMounted = true;

    const getAllGalleryData = async () => {
      try {
        setLoadingGallery(true);

        const res =
          await imageGalleryService.GetImageListPublic(
            1,
            lang,
            "-1"
          );

        if (!isMounted) return;

        const apiData = res?.data?.table;

        if (!Array.isArray(apiData)) {
          setAllGalleryData([]);
          return;
        }

        const formattedData = apiData.map(
          (item, index) => ({
            key:
              item.imageId ??
              item.docUniqueId ??
              index + 1,

            imageId: item.imageId,

            description: item.description,

            imageCategoryType:
              String(item.imageCategoryType),

            imagenotes: item.imagenotes,

            doc: {
              docMimeType: item.docMimeType,

              docUniqueId: item.docUniqueId,
            },

            // शुरुआत में image loading image रहेगी
            ImgUrl: loadingImg,
          })
        );

        setAllGalleryData(formattedData);
      } catch (error) {
        console.error(
          "Error fetching gallery data:",
          error
        );

        if (isMounted) {
          setAllGalleryData([]);
        }
      } finally {
        if (isMounted) {
          setLoadingGallery(false);
        }
      }
    };

    getAllGalleryData();

    return () => {
      isMounted = false;
    };
  }, [imageGalleryService, lang]);

  // =========================================================
  // FILTER DATA
  //
  // यहाँ कोई API call नहीं है।
  //
  // Dropdown बदलने पर सिर्फ JavaScript filter चलेगा।
  // =========================================================

  useEffect(() => {
    let data = [...allGalleryData];

    if (filterType !== "-1") {
      data = data.filter(
        (item) =>
          String(item.imageCategoryType) ===
          String(filterType)
      );
    }

    setFilteredData(data);

    // Filter बदलने पर हमेशा first page पर जाएँ
    setCurrentPage(1);

    // पुराने page images clear
    setPageImages({});
  }, [allGalleryData, filterType]);

  // =========================================================
  // DROPDOWN CHANGE
  // =========================================================

  const loadTableFilter = useCallback((value) => {
    setFilterType(String(value));
  }, []);

  // =========================================================
  // PAGINATION DATA
  // =========================================================

  const currentImages = useMemo(() => {
    const indexOfFirstItem =
      (currentPage - 1) * itemsPerPage;

    const indexOfLastItem =
      indexOfFirstItem + itemsPerPage;

    return filteredData.slice(
      indexOfFirstItem,
      indexOfLastItem
    );
  }, [
    filteredData,
    currentPage,
    itemsPerPage,
  ]);

  // =========================================================
  // DOWNLOAD SINGLE IMAGE
  //
  // पहले cache check होगा।
  //
  // अगर image पहले download हो चुकी है:
  //
  // cache से तुरंत मिलेगी।
  //
  // अगर नहीं:
  //
  // API से download होगी।
  // =========================================================

  const getImageById = useCallback(
    async (docUniqueId, docMimeType) => {
      if (!docUniqueId) {
        return demoUser;
      }

      // ---------------------------------------------
      // CACHE CHECK
      // ---------------------------------------------

      if (
        imageCache.current.has(
          String(docUniqueId)
        )
      ) {
        return imageCache.current.get(
          String(docUniqueId)
        );
      }

      // ---------------------------------------------
      // API IMAGE DOWNLOAD
      // ---------------------------------------------

      try {
        const res =
          await imageGalleryService.ViewDocument(
            docUniqueId
          );

        if (res?.status === 200) {
          const fileBlob = new Blob(
            [res.data],
            {
              type:
                docMimeType ||
                "application/octet-stream",
            }
          );

          const objectUrl =
            URL.createObjectURL(fileBlob);

          // -----------------------------------------
          // CACHE
          // -----------------------------------------

          imageCache.current.set(
            String(docUniqueId),
            objectUrl
          );

          return objectUrl;
        }
      } catch (error) {
        console.error(
          "Error fetching image:",
          docUniqueId,
          error
        );
      }

      return demoUser;
    },
    [imageGalleryService]
  );

  // =========================================================
  // LOAD ONLY CURRENT PAGE IMAGES
  //
  // IMPORTANT:
  //
  // यहाँ सिर्फ current page की images download होंगी।
  //
  // Example:
  //
  // Page 1 = image 1-9
  // Page 2 = image 10-18
  // Page 3 = image 19-27
  //
  // =========================================================

  useEffect(() => {
    let cancelled = false;

    const loadCurrentPageImages = async () => {
      if (!currentImages.length) {
        return;
      }

      const imageResults = {};

      // ---------------------------------------------------
      // सिर्फ current page की images
      // ---------------------------------------------------

      await Promise.all(
        currentImages.map(async (item) => {
          const id =
            item.doc?.docUniqueId;

          if (!id) {
            return;
          }

          const cacheKey = String(id);

          // ---------------------------------------------
          // अगर cache में image है तो API call नहीं
          // ---------------------------------------------

          if (
            imageCache.current.has(
              cacheKey
            )
          ) {
            imageResults[item.key] =
              imageCache.current.get(
                cacheKey
              );

            return;
          }

          // ---------------------------------------------
          // Image download
          // ---------------------------------------------

          const imageUrl =
            await getImageById(
              item.doc.docUniqueId,
              item.doc.docMimeType
            );

          imageResults[item.key] =
            imageUrl;
        })
      );

      if (!cancelled) {
        setPageImages(imageResults);
      }
    };

    loadCurrentPageImages();

    return () => {
      cancelled = true;
    };
  }, [
    currentImages,
    getImageById,
  ]);

  // =========================================================
  // PAGINATION CHANGE
  // =========================================================

  const handlePaginationChange = useCallback(
    (page, pageSize) => {
      setCurrentPage(page);

      setItemsPerPage(pageSize);

      // New page के पुराने image state को clear
      setPageImages({});
    },
    []
  );

  // =========================================================
  // CLEANUP OBJECT URL
  //
  // Component unmount होने पर सभी cached Blob URLs
  // release कर देंगे।
  // =========================================================

  useEffect(() => {
    return () => {
      imageCache.current.forEach(
        (url) => {
          if (
            url &&
            url.startsWith("blob:")
          ) {
            URL.revokeObjectURL(url);
          }
        }
      );

      imageCache.current.clear();
    };
  }, []);

  // =========================================================
  // RENDER
  // =========================================================

  return (
    <>
      <BreadcrumbLayout title="Map Gallery" />

      <Container fluid>
        <Row>

          {/* =================================================
              SIDEBAR
          ================================================= */}

          <PhotoGallerySidebar
            menuData={menuData}
          />

          {/* =================================================
              CONTENT
          ================================================= */}

          <Col
            md={9}
            className="content order-md-last order-first"
          >
            <Row>

              {/* =================================================
                  HEADING
              ================================================= */}

              <Col
                xs={12}
                className="heading"
              >
                <h3>
                  {t("breadcrumb.photo-gallery")}
                </h3>
              </Col>

              <Col
                xs={12}
                className="mb-1 mt-1"
              >

                {/* =================================================
                    FILTER
                ================================================= */}

                <Row>
                  <Col
                    lg={12}
                    className="mb-2"
                  >
                    <div className="img-category">

                      <Form
                        form={form}
                        initialValues={{
                          filterType: "-1",
                        }}
                      >

                        <Form.Item
                          name="filterType"
                          label={
                            lang === 1
                              ? "छवि श्रेणी चुनें:"
                              : "Select Image Category:"
                          }
                          labelCol={{
                            style: {
                              fontWeight:
                                "bold",
                            },
                          }}
                        >

                          <Select
                            showSearch
                            placeholder={
                              lang === 1
                                ? "श्रेणी चुनें"
                                : "Select Category"
                            }
                            loading={
                              loadingDDL
                            }
                            optionFilterProp="label"
                            onChange={
                              loadTableFilter
                            }
                            options={
                              filterListData
                            }
                            filterOption={(
                              input,
                              option
                            ) =>
                              (
                                option?.label ??
                                ""
                              )
                                .toLowerCase()
                                .includes(
                                  input.toLowerCase()
                                )
                            }
                          />

                        </Form.Item>

                      </Form>

                    </div>
                  </Col>
                </Row>

                {/* =================================================
                    LOADING
                ================================================= */}

                {loadingGallery && (
                  <div
                    style={{
                      textAlign:
                        "center",
                      padding:
                        "20px",
                    }}
                  >
                    <img
                      src={Animation}
                      alt="Loading"
                    />
                  </div>
                )}

                {/* =================================================
                    GALLERY
                ================================================= */}

                {currentImages.length >
                0 ? (
                  <>

                    <Row className="galleryWrap">

                      <Image.PreviewGroup>

                        {currentImages.map(
                          (slide) => {

                            const imageUrl =
                              pageImages[
                                slide.key
                              ] ||
                              loadingImg;

                            return (
                              <Col
                                lg={4}
                                md={6}
                                sm={6}
                                xs={12}
                                key={
                                  slide.key
                                }
                              >

                                <Image
                                  src={
                                    imageUrl
                                  }
                                  fallback={
                                    demoUser
                                  }
                                  alt={
                                    slide.description ||
                                    "Gallery Image"
                                  }
                                />

                                <p className="image-description">
                                  {
                                    slide.description
                                  }
                                </p>

                              </Col>
                            );
                          }
                        )}

                      </Image.PreviewGroup>

                    </Row>

                    {/* =================================================
                        PAGINATION
                    ================================================= */}

                    <Row justify="center">

                      <Col>

                        <div
                          className="img-pagination"
                          style={{
                            paddingBottom:
                              "20px",
                          }}
                        >

                          <Pagination
                            current={
                              currentPage
                            }
                            pageSize={
                              itemsPerPage
                            }
                            total={
                              filteredData.length
                            }
                            onChange={
                              handlePaginationChange
                            }
                            showSizeChanger
                            pageSizeOptions={[
                              "9",
                              "18",
                              "36",
                              "72",
                            ]}
                          />

                        </div>

                      </Col>

                    </Row>

                  </>
                ) : (

                  /* =================================================
                      EMPTY
                  ================================================= */

                  !loadingGallery && (
                    <Row>
                      <Col>

                        <Empty
                          description={
                            lang === 1 ? (
                              <span
                                style={{
                                  fontWeight:
                                    "bold",
                                }}
                              >
                                वर्तमान में,
                                प्रदर्शित करने
                                के लिए कोई
                                डेटा नहीं है ।
                              </span>
                            ) : (
                              <span
                                style={{
                                  fontWeight:
                                    "bold",
                                }}
                              >
                                Currently,
                                There are no
                                data to
                                display.
                              </span>
                            )
                          }
                        />

                      </Col>
                    </Row>
                  )
                )}

              </Col>

            </Row>
          </Col>

        </Row>
      </Container>
    </>
  );
}